import { SvgIcon, SvgIconTypeMap, SxProps } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { Dispatch, ReactElement, ReactNode, SetStateAction } from 'react';
import type { EntityName, EntitySecurity, FeatureName, SecurityAction } from '../../auth/Security';
import { BreakpointSize } from '../../hooks/breakpoints';
import type { ActionType } from '../ActionType';

type IconComponentType = OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;

export type MainSidePanelProps = {
    /**
     * Toolbar visibility for these panels is RBAC-gated.
     * Individual panel components are responsible for enforcing in-panel action permissions
     * (for example edit/delete controls) after panel open.
     */
    securedPanels?: SecuredSidePanelDef[];

    /**
     * Main content that gets augmented with a side panel. This is typically the main content of the page.
     */
    children: ReactNode | ReactNode[];

    /**
     * Toolbar content that appears to the left of the header that is rendered above the main content and
     * the side panel. Typically used to display breadcrumbs.
     */
    leftToolbar: ReactElement;

    /**
     * Toolbar content that appears to the right of the header that is rendered above the main content and
     * the side panel. Typically used to display custom action buttons, in addition to the standard ones that appear
     * in this area - e.g. close, expand, and collapse buttons.
     */
    rightToolbar?: ReactElement;

    /**
     * When true, the header that is rendered above the main content and the side panel is hidden.
     */
    hideHeader?: boolean;

    /**
     * Custom styles for the side panel container.
     */
    panelBoxSx?: any;

    /**
     * Controls if the side panel is expanded to a larger width.
     */
    expandView?: boolean;

    /**
     * Controls which side panel is currently visible. If not provided, the side panel will manage its own state.
     */
    visibleSidePanelId?: string;

    /**
     * Controls the side panel visibility state. If not provided, the side panel will manage its own state.
     * Only used by the `DeepLinkingSidePanel` component.
     */
    visiblePanelId?: string | undefined;

    /**
     * Callback that is called when the side panel visibility state changes.
     * Only used by the `DeepLinkingSidePanel` component.
     */
    onPanelChange?: Dispatch<SetStateAction<string | undefined>>;

    /**
     * If provided the entire component composition, including header, the main content and the side panel
     * will be wrapped by the entity security provider, to which these props will be relayed.
     */
    entitySecurityProviderProps?: {
        entityName: EntityName;
        entitySecurity: EntitySecurity;
    };

    /**
     * When `true` the side panel open/close state changes will be synchronized to the deep link UI state.
     */
    enableDeepLink?: boolean;
};

export type PanelProps = {
    id: string;
    children: (onClose: () => void) => ReactNode;
    label?: ReactNode;
    icon?: IconComponentType;
    /** When true, panel can open programmatically but is omitted from the toolbar. */
    hidden?: boolean;
    disabled?: boolean;
    tooltip?: ActionType['tooltip'];
    securityAction?: SecurityAction;
    featureName?: FeatureName;
    entityName?: EntityName;
    entitySecurity?: EntitySecurity;
};

export type SidePanelDef = {
    id: string;
    icon: typeof SvgIcon;
    component: (onClose: () => void) => ReactElement;
    label?: string | ReactElement;
    disabled?: boolean;
    hidden?: boolean;
    tooltip?: string | ReactElement;
    onClick?: () => Promise<any> | void;
};

export type SecuredSidePanelDef = SidePanelDef & {
    securityAction: SecurityAction;
    featureName?: FeatureName;
    entityName?: EntityName;
    entitySecurity?: EntitySecurity;
};

export type SidePanelSx = {
    [key in BreakpointSize]: SxProps;
};

export function getActionColor(action: ActionType) {
    return action.disabled ? 'text.disabled' : action.selected ? 'primary.main' : 'text.primary';
}
